import { useCallback, useMemo } from 'react';

import { ActionMeta, GroupBase, OnChangeValue } from 'react-select';

import { isEmpty, isNotDefined } from '@hsp/utilities';

import { isMultiData } from 'components/select/utilities/is-multi-data.guard';
import { SelectProps } from 'types/components/select';

export const useIsFixed = <
  IsMulti extends boolean = false,
  Option = unknown,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  isClearable,
  isFixed,
  onChange,
  options,
  value,
}: SelectProps<IsMulti, Option, Group>) => {
  const clearable = useMemo(() => {
    if (isNotDefined(isFixed) || isNotDefined(value)) {
      return isClearable ?? false;
    }

    if (isMultiData(value)) {
      return value.some((item) => !isFixed(item));
    }

    return !isFixed(value);
  }, [isFixed, isClearable, value]);

  const changed = useCallback(
    (newValue: OnChangeValue<Option, IsMulti>, actionMeta: ActionMeta<Option>) => {
      const { action, removedValue } = actionMeta;

      if (isNotDefined(options) || isEmpty(options)) {
        return;
      }

      switch (action) {
        case 'pop-value':

        case 'remove-value':
          if (isFixed?.(removedValue)) {
            return;
          }
          break;

        case 'clear': {
          if (isMultiData(value)) {
            newValue = value.filter((current) => isFixed?.(current)) as OnChangeValue<Option, IsMulti>;
          }
          break;
        }
      }

      onChange?.(newValue, actionMeta);
    },
    [isFixed, onChange, options, value]
  );

  return { changed, clearable };
};
